import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileDown, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { PageHeader } from "@/components/app/PageHeader";

export default function AdminQrPosterPage() {
  const { t, lang } = useI18n();
  const [config, setConfig] = useState({ center_code: "MAIN-OFFICE", wall_qr_payload: "TUITION://MAIN-OFFICE" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/config/qr-poster.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "wall-qr-poster.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(lang === "ms" ? "Gagal memuat turun poster" : "Failed to download poster");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-qr-poster">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("wallQR")}
        subtitle={lang === "ms"
          ? "Cetak poster QR ini dan lekatkan di dinding pejabat. Ibu bapa akan mengimbasnya untuk daftar masuk anak."
          : "Print this QR poster and stick it on the office wall. Parents will scan it to check their children in."}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.1s" }}>
          <div className="h-1.5 w-full bg-gradient-to-r from-orange-600 to-yellow-400" />
          <CardContent className="p-6 text-center">
            <h2 className="flex items-center justify-center gap-2 font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">
              <QrCode className="h-5 w-5 text-orange-600" /> {t("wallQR")}
            </h2>
            <div className="mt-4 grid place-items-center">
              <div className="rounded-[1.5rem] border-4 border-blue-950/10 bg-white p-5 shadow-lg">
                <QRCodeSVG
                  value={config.wall_qr_payload || `TUITION://${config.center_code}`}
                  size={240}
                  bgColor="#ffffff"
                  fgColor="#0A5C36"
                />
              </div>
            </div>
            <p className="num mt-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Center code: <span className="text-blue-950">{config.center_code}</span></p>
          </CardContent>
        </Card>

        <Card className="pmm-pop overflow-hidden rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.18s" }}>
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 to-blue-500" />
          <CardContent className="p-6">
            <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">{lang === "ms" ? "Cetak poster (PDF)" : "Print poster (PDF)"}</h2>
            <p className="mt-2 text-sm font-medium text-muted-foreground">
              {lang === "ms"
                ? "Muat turun poster A4 sedia cetak dengan QR besar, arahan dwibahasa dan kod pusat."
                : "Download a print-ready A4 poster with a large QR, bilingual instructions and the center code."}
            </p>
            <Button
              onClick={downloadPdf}
              disabled={busy}
              className="mt-4 h-11 gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105"
              data-testid="download-poster-btn"
            >
              <FileDown className="h-4 w-4" /> {lang === "ms" ? "Muat turun PDF" : "Download PDF"}
            </Button>
            <ul className="mt-6 space-y-2 text-sm font-medium text-muted-foreground">
              <li>• {lang === "ms" ? "Hanya admin boleh melihat & mencetak poster ini." : "Only admins can view & print this poster."}</li>
              <li>• {lang === "ms" ? "Ibu bapa membuka aplikasi, pilih anak dan imbas QR ini." : "Parents open the app, pick a child and scan this QR."}</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
